import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
  totalMs: number;
}

@Component({
  selector: 'app-countdown-timer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="countdown-container"
      [class.expired]="countdown.isExpired"
      [class.pending]="isPendingListing()"
    >
      <div class="countdown-header">
        <span class="countdown-icon">{{ getCountdownIcon() }}</span>
        <span class="countdown-label">
          {{ getCountdownLabel() }}
        </span>
      </div>

      <div class="countdown-pending" *ngIf="isPendingListing()">
        <span class="pending-icon">⏳</span>
        <span class="pending-text"
          >Expiry date will be set once listing is approved</span
        >
      </div>

      <div
        class="countdown-display"
        *ngIf="!countdown.isExpired && !isPendingListing()"
      >
        <div class="countdown-segment" *ngIf="countdown.days > 0">
          <span class="countdown-number">{{ countdown.days }}</span>
          <span class="countdown-unit">{{
            countdown.days === 1 ? 'Day' : 'Days'
          }}</span>
        </div>

        <div class="countdown-segment">
          <span class="countdown-number">{{
            formatNumber(countdown.hours)
          }}</span>
          <span class="countdown-unit">Hours</span>
        </div>

        <div class="countdown-separator" *ngIf="countdown.days === 0">:</div>

        <div class="countdown-segment">
          <span class="countdown-number">{{
            formatNumber(countdown.minutes)
          }}</span>
          <span class="countdown-unit">Minutes</span>
        </div>

        <div class="countdown-separator" *ngIf="countdown.days === 0">:</div>

        <div class="countdown-segment" *ngIf="countdown.days === 0">
          <span class="countdown-number">{{
            formatNumber(countdown.seconds)
          }}</span>
          <span class="countdown-unit">Seconds</span>
        </div>
      </div>

      <div
        class="countdown-expired"
        *ngIf="countdown.isExpired && !isPendingListing()"
      >
        <span class="expired-icon">🚫</span>
        <span class="expired-text">This listing has expired</span>
      </div>

      <div
        class="countdown-status"
        *ngIf="!countdown.isExpired && !isPendingListing()"
      >
        <div
          class="status-indicator"
          [class]="getStatusClass(countdown.totalMs)"
        ></div>
        <span class="status-text">{{ getStatusText(countdown.totalMs) }}</span>
      </div>
    </div>
  `,
  styles: [
    `
      .countdown-container {
        padding: 16px;
        background: #f8fafc;
        border-radius: 8px;
        border: 1px solid #e5e7eb;
      }

      .countdown-container.expired {
        background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
        border-color: rgba(239, 68, 68, 0.3);
      }

      .countdown-container.pending {
        background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
        border-color: rgba(245, 158, 11, 0.3);
      }

      .countdown-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 16px;
        justify-content: center;
      }

      .countdown-icon {
        font-size: 1.2rem;
        color: #3b82f6;
      }

      .expired .countdown-icon {
        color: #ef4444;
      }

      .pending .countdown-icon {
        color: #f59e0b;
      }

      .countdown-label {
        font-size: 1.1rem;
        font-weight: 700;
        color: #1e293b;
      }

      .countdown-pending {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 16px;
        background: rgba(245, 158, 11, 0.1);
        border-radius: 12px;
        margin-bottom: 12px;
      }

      .pending-icon {
        font-size: 1.5rem;
      }

      .pending-text {
        font-size: 1rem;
        font-weight: 600;
        color: #92400e;
        text-align: center;
      }

      .countdown-display {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        flex-wrap: wrap;
        margin-bottom: 12px;
      }

      .countdown-segment {
        display: flex;
        flex-direction: column;
        align-items: center;
        background: rgba(59, 130, 246, 0.1);
        padding: 12px 16px;
        border-radius: 12px;
        min-width: 70px;
        transition: all 0.3s ease;
      }

      .countdown-segment:hover {
        background: rgba(59, 130, 246, 0.15);
        transform: scale(1.05);
      }

      .countdown-number {
        font-size: 1.8rem;
        font-weight: 800;
        color: #1e293b;
        line-height: 1;
        margin-bottom: 4px;
      }

      .countdown-unit {
        font-size: 0.8rem;
        font-weight: 600;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .countdown-separator {
        font-size: 1.5rem;
        font-weight: 800;
        color: #3b82f6;
        margin: 0 4px;
      }

      .countdown-expired {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 16px;
        background: rgba(239, 68, 68, 0.1);
        border-radius: 12px;
        margin-bottom: 12px;
      }

      .expired-icon {
        font-size: 1.5rem;
      }

      .expired-text {
        font-size: 1.1rem;
        font-weight: 700;
        color: #dc2626;
      }

      .countdown-status {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        font-size: 0.9rem;
        font-weight: 600;
      }

      .status-indicator {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        transition: all 0.3s ease;
      }

      .status-indicator.critical {
        background: #ef4444;
        box-shadow: 0 0 10px rgba(239, 68, 68, 0.5);
        animation: pulse 1.5s infinite;
      }

      .status-indicator.warning {
        background: #f59e0b;
        box-shadow: 0 0 8px rgba(245, 158, 11, 0.4);
      }

      .status-indicator.good {
        background: #10b981;
        box-shadow: 0 0 6px rgba(16, 185, 129, 0.3);
      }

      .status-text {
        color: #64748b;
      }

      .status-text.critical {
        color: #dc2626;
      }

      .status-text.warning {
        color: #d97706;
      }

      .status-text.good {
        color: #059669;
      }

      @keyframes pulse {
        0%,
        100% {
          opacity: 1;
          transform: scale(1);
        }
        50% {
          opacity: 0.7;
          transform: scale(1.2);
        }
      }

      @media (max-width: 768px) {
        .countdown-container {
          padding: 16px;
        }

        .countdown-display {
          gap: 8px;
        }

        .countdown-segment {
          min-width: 60px;
          padding: 10px 12px;
        }

        .countdown-number {
          font-size: 1.5rem;
        }

        .countdown-unit {
          font-size: 0.7rem;
        }
      }

      @media (max-width: 480px) {
        .countdown-display {
          gap: 6px;
        }

        .countdown-segment {
          min-width: 50px;
          padding: 8px 10px;
        }

        .countdown-number {
          font-size: 1.3rem;
        }

        .pending-text {
          font-size: 0.9rem;
        }
      }
    `,
  ],
})
export class CountdownTimerComponent implements OnInit, OnDestroy {
  @Input() expiresAt: string | Date | null = null;
  @Input() showSeconds: boolean = true;
  @Input() updateInterval: number = 1000;
  @Input() listingStatus: string = 'active';

  countdown: CountdownTime = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false,
    totalMs: 0,
  };

  private intervalId: any;

  ngOnInit(): void {
    if (this.isPendingListing()) {
      return;
    }

    if (!this.expiresAt) {
      console.warn('CountdownTimer: expiresAt is required');
      this.countdown.isExpired = true;
      return;
    }

    this.startCountdown();
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  private startCountdown(): void {
    this.updateCountdown();

    this.intervalId = setInterval(() => {
      this.updateCountdown();
    }, this.updateInterval);
  }

  private updateCountdown(): void {
    if (!this.expiresAt || this.isPendingListing()) {
      this.countdown = {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isExpired: true,
        totalMs: 0,
      };
      return;
    }

    const now = new Date().getTime();
    const expiryTime = new Date(this.expiresAt).getTime();

    const placeholderDate = new Date('2099-12-31').getTime();
    if (expiryTime === placeholderDate) {
      this.countdown = {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isExpired: false,
        totalMs: 0,
      };
      return;
    }

    const timeDiff = expiryTime - now;

    if (timeDiff <= 0) {
      this.countdown = {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isExpired: true,
        totalMs: 0,
      };

      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
      return;
    }

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

    this.countdown = {
      days,
      hours,
      minutes,
      seconds,
      isExpired: false,
      totalMs: timeDiff,
    };
  }

  isPendingListing(): boolean {
    return this.listingStatus === 'pending';
  }

  getCountdownIcon(): string {
    if (this.isPendingListing()) {
      return '⏳';
    }
    if (this.countdown.isExpired) {
      return '🚫';
    }
    return '⏰';
  }

  getCountdownLabel(): string {
    if (this.isPendingListing()) {
      return 'Pending Approval';
    }
    if (this.countdown.isExpired) {
      return 'Listing Expired';
    }
    return 'Expires In';
  }

  formatNumber(num: number): string {
    return num.toString().padStart(2, '0');
  }

  getStatusClass(totalMs: number): string {
    const hours = totalMs / (1000 * 60 * 60);

    if (hours < 24) return 'critical';
    if (hours < 72) return 'warning';
    return 'good';
  }

  getStatusText(totalMs: number): string {
    const hours = totalMs / (1000 * 60 * 60);

    if (hours < 24) return 'Expires within 24 hours';
    if (hours < 72) return 'Expires within 3 days';
    return 'Active listing';
  }
}
